/**
 * Hero CTA → Slack (#delfuocampo) + PostHog replay + engagement dropoff.
 * Best-effort: nunca rompe UX.
 */
import { detectTrafficSource } from "@/lib/detectTrafficSource";
import { isInstagramBrowser } from "@/lib/instagramBrowserDetection";
import {
  getPostHogSlackSessionPropertiesPostHog,
  getSlackFunnelDiagnosticsExtraPostHog,
  isPostHogConfigured,
  startSessionRecordingPostHog,
} from "@/lib/posthogAnalytics";
import { resolveHeroCtaResolvedType } from "@/lib/heroCtaAdminMap";
import {
  captureVisitorJourneyRealtime,
  getJourneyPayloadForServer,
} from "@/lib/posthogVisitorJourney";
import {
  getServerTemplateConfig,
  getWorkerGeoAnalyticsProps,
  type ServerTemplateConfig,
} from "@/lib/serverTemplateDetection";

export type HeroCtaFunnelKind =
  | "page_enter_hero_landing"
  | "hero_cta_impression"
  | "cta_click_hero_checkout"
  | "cta_click_hero_community"
  | "cta_click_hero_mejores_amigos"
  | "cta_click_hero_birthday"
  | "cta_click_hero_admin";

export type HeroCtaResolvedType =
  | "checkout"
  | "community"
  | "mejores_amigos"
  | "birthday"
  | "wishlist"
  | "admin_override"
  | "none";

/** Cascada admin (WishlistAdmin): barrio → ciudad → provincia → país → default. */
export type CtaGeoPriorityLabel =
  | "Barrio CABA/GBA"
  | "Ciudad AR"
  | "Provincia AR"
  | "País"
  | "DEFAULT";

const CTA_DESTINATION_BY_TYPE: Record<string, string> = {
  wishlist: "/fan/wishlist",
  community: "/invitacion-premium",
  mejores_amigos: "mercadopago_checkout",
  birthday: "/r/cumple",
  checkout: "/checkout-channel-invite",
};

/** Mapea heroCtaSource del Worker a la prioridad geo del operador. */
export function resolveCtaGeoPriorityLabel(
  source?: string | null,
): CtaGeoPriorityLabel | undefined {
  const s = (source || "").trim().toLowerCase();
  if (!s) return undefined;
  if (s === "neighborhood_ar") return "Barrio CABA/GBA";
  if (s === "city_ar") return "Ciudad AR";
  if (
    s === "province_ar" ||
    s === "province_ar_none" ||
    s === "province_ar_none_killswitch" ||
    s === "metro_premium_override"
  ) {
    return "Provincia AR";
  }
  if (s === "country" || s === "country_ar" || s === "country_db" || s === "edge_intl") {
    return "País";
  }
  if (
    s === "country_default" ||
    s === "no_entry_fallback" ||
    s === "edge_fallback" ||
    s === "edge_when_province_unknown" ||
    s === "global_hide_hero_cta" ||
    s === "kill_switch" ||
    s === "template_no_hero" ||
    s === "no_db" ||
    s === "db_error"
  ) {
    return "DEFAULT";
  }
  return "DEFAULT";
}

export function formatCtaGeoSourceHuman(source?: string | null): string | undefined {
  const s = (source || "").trim();
  if (!s) return undefined;
  if (s === "neighborhood_ar") return "neighborhood_ar (barrio)";
  if (s === "city_ar") return "city_ar (ciudad)";
  if (s === "province_ar") return "province_ar (provincia)";
  if (s === "province_ar_none" || s === "province_ar_none_killswitch") {
    return `${s} (provincia sin botón)`;
  }
  if (s === "country" || s === "country_ar" || s === "country_db") return `${s} (país)`;
  if (s === "country_default") return "country_default (global)";
  return s;
}

export function resolveCtaDestinationPath(
  heroCtaType: HeroCtaResolvedType | string | null | undefined,
  opts?: { href?: string; isArgentina?: boolean },
): string | undefined {
  const href = opts?.href?.trim();
  if (href) return href.slice(0, 500);
  const t = (heroCtaType || "").trim().toLowerCase();
  if (!t || t === "none" || t === "admin_override") return undefined;
  if (t === "checkout") {
    return opts?.isArgentina ? "mercadopago_checkout" : CTA_DESTINATION_BY_TYPE.checkout;
  }
  return CTA_DESTINATION_BY_TYPE[t];
}

/** Props comunes PostHog + Slack para hero CTA (geo Worker + prioridad cascada). */
export function buildHeroCtaSessionContext(opts?: {
  ctaResolved?: string;
  ctaGeoSource?: string;
  heroCtaType?: HeroCtaResolvedType | string;
  href?: string;
  isArgentina?: boolean;
}): Record<string, string | number | boolean | undefined> {
  const cfg = getServerTemplateConfig();
  const ctaGeoSource = opts?.ctaGeoSource ?? cfg?.heroCtaSource;
  const ctaResolved = opts?.ctaResolved ?? cfg?.heroCtaResolved;
  const priority = resolveCtaGeoPriorityLabel(ctaGeoSource);
  const heroType = opts?.heroCtaType;
  const destination = resolveCtaDestinationPath(heroType, {
    href: opts?.href ?? cfg?.ctaUrl,
    isArgentina: opts?.isArgentina,
  });

  const out: Record<string, string | number | boolean | undefined> = {
    ...(ctaResolved ? { cta_resolved: ctaResolved } : {}),
    ...(ctaGeoSource ? { cta_geo_source: ctaGeoSource } : {}),
    ...(ctaGeoSource
      ? { cta_geo_source_human: formatCtaGeoSourceHuman(ctaGeoSource) }
      : {}),
    ...(priority ? { cta_geo_priority: priority } : {}),
    ...(heroType ? { hero_cta_type: heroType } : {}),
    ...(destination ? { cta_destination: destination } : {}),
    worker_config_cache_hint: "wishlist_kv_60s",
  };

  const geo = getWorkerGeoAnalyticsProps();
  for (const [k, v] of Object.entries(geo)) {
    if (v !== undefined && v !== "") out[k] = v;
  }
  return out;
}

export interface HeroCtaFlagsSnapshot {
  hideCta?: boolean;
  showCheckout?: boolean;
  showCommunity?: boolean;
  showBestFriends?: boolean;
  showBirthday?: boolean;
  showWishlist?: boolean;
  hasCheckoutRoute?: boolean;
  ctaUrl?: string;
  ctaLabel?: string;
  heroCtaResolved?: string;
  heroCtaSource?: string;
  fromFreshFetch?: boolean;
  showCtaSkeleton?: boolean;
}

export function resolveActiveHeroCtaType(opts: {
  hideCta: boolean;
  showCtaSkeleton: boolean;
  hasAdminHeroCta?: boolean;
  showCheckout: boolean;
  hasCheckoutRoute: boolean;
  showCommunity: boolean;
  showBestFriends: boolean;
  showBirthday: boolean;
  showWishlist: boolean;
  ctaUrl?: string;
}): HeroCtaResolvedType | null {
  return resolveHeroCtaResolvedType(opts);
}

export function heroClickKindFromElementId(elementId: string): HeroCtaFunnelKind | null {
  switch (elementId) {
    case "hero_cta_checkout":
      return "cta_click_hero_checkout";
    case "hero_cta_community":
      return "cta_click_hero_community";
    case "hero_cta_mejores_amigos":
      return "cta_click_hero_mejores_amigos";
    case "hero_cta_birthday":
      return "cta_click_hero_birthday";
    case "hero_cta_admin_override":
      return "cta_click_hero_admin";
    default:
      return null;
  }
}

export function buildHeroCtaFlagsSnapshot(opts: {
  hideCta: boolean;
  showCheckout: boolean;
  showCommunity: boolean;
  showBestFriends: boolean;
  showBirthday: boolean;
  showWishlist: boolean;
  hasCheckoutRoute: boolean;
  showCtaSkeleton: boolean;
  fromFreshFetch?: boolean;
  ctaUrl?: string;
  ctaLabel?: string;
  heroCtaResolved?: string;
  heroCtaSource?: string;
}): HeroCtaFlagsSnapshot {
  return {
    hideCta: opts.hideCta,
    showCheckout: opts.showCheckout,
    showCommunity: opts.showCommunity,
    showBestFriends: opts.showBestFriends,
    showBirthday: opts.showBirthday,
    showWishlist: opts.showWishlist,
    hasCheckoutRoute: opts.hasCheckoutRoute,
    showCtaSkeleton: opts.showCtaSkeleton,
    fromFreshFetch: opts.fromFreshFetch,
    ctaUrl: opts.ctaUrl,
    ctaLabel: opts.ctaLabel,
    heroCtaResolved: opts.heroCtaResolved,
    heroCtaSource: opts.heroCtaSource,
  };
}

export function getHeroCtaSessionId(): string {
  const ph = getPostHogSlackSessionPropertiesPostHog();
  return ph.ph_session_id || `hero_${Date.now()}`;
}

function canPostFunnelApi(): boolean {
  if (typeof window === "undefined") return false;
  if (!import.meta.env.DEV) return true;
  const proxy = (import.meta.env.VITE_DEV_API_PROXY as string | undefined)?.trim();
  return Boolean(proxy);
}

function flagsSnapshotString(flags?: HeroCtaFlagsSnapshot): string | undefined {
  if (!flags) return undefined;
  const parts: string[] = [];
  if (flags.hideCta) parts.push("hide");
  if (flags.showCheckout) parts.push("checkout");
  if (flags.showCommunity) parts.push("community");
  if (flags.showBestFriends) parts.push("mejores_amigos");
  if (flags.showBirthday) parts.push("birthday_legacy");
  if (flags.showWishlist) parts.push("wishlist");
  if (flags.hasCheckoutRoute === false) parts.push("no_checkout_route");
  if (flags.fromFreshFetch) parts.push("fresh_fetch");
  if (flags.showCtaSkeleton) parts.push("skeleton");
  const resolved = flags.heroCtaResolved || flags.heroCtaSource;
  if (resolved) parts.push(`resolved=${resolved}`);
  return parts.length ? parts.join(",") : undefined;
}

/** Contexto rico para Slack + PostHog (geo Worker, device, UTM, flags CTA). */
export function buildHeroCtaReportExtra(opts?: {
  heroCtaType?: string;
  flags?: HeroCtaFlagsSnapshot;
  templateConfig?: ServerTemplateConfig | null;
  replayNearNow?: boolean;
  extra?: Record<string, string | number | boolean>;
}): Record<string, string | number | boolean> {
  const cfg = opts?.templateConfig ?? getServerTemplateConfig();
  const geo = getWorkerGeoAnalyticsProps();
  const ts = detectTrafficSource();
  const funnelDiag = getSlackFunnelDiagnosticsExtraPostHog(
    opts?.replayNearNow ? { replayNearNow: true } : undefined,
  );

  const out: Record<string, string | number | boolean> = {
    funnel: "hero_cta",
    product_surface: "hero",
    cta_config_source: "cta_por_region",
    embedded_social_browser: isInstagramBrowser(),
    channel: ts.channel,
    source: ts.source,
    medium: ts.medium,
    ...(ts.inAppBrowser || isInstagramBrowser() ? { in_app_browser: true } : {}),
    ...(ts.raw.referrer_domain ? { referrer: ts.raw.referrer_domain } : {}),
    ...funnelDiag,
  };

  if (opts?.heroCtaType) out.hero_cta_type = opts.heroCtaType;
  const flagStr = flagsSnapshotString(opts?.flags);
  if (flagStr) out.cta_flags_snapshot = flagStr;

  if (cfg) {
    if (cfg.templateName) out.template_name = cfg.templateName;
    if (cfg.detectionMethod) out.detection_method = cfg.detectionMethod;
    if (cfg.provinceDetectionMethod) {
      out.province_detection_method = cfg.provinceDetectionMethod;
    }
    if (cfg.province) out.worker_province = cfg.province;
    if (cfg.city) out.worker_city = cfg.city;
    if (cfg.neighborhood) out.worker_neighborhood = cfg.neighborhood;
    if (cfg.geoSource) out.geo_source = cfg.geoSource;
    if (cfg.heroCtaResolved) out.cta_resolved = cfg.heroCtaResolved;
    if (cfg.heroCtaSource) out.cta_geo_source = cfg.heroCtaSource;
    if (cfg.vpnDetected) out.worker_vpn_detected = true;
    if (cfg.isHosting) out.worker_is_hosting = true;
    if (cfg.asOrganization) out.worker_asn_org = cfg.asOrganization.slice(0, 120);
    if (cfg.ctaUrl) out.cta_href_config = cfg.ctaUrl.slice(0, 500);
    if (cfg.ctaLabel) out.cta_label_config = cfg.ctaLabel.slice(0, 120);
  }

  for (const [k, v] of Object.entries(geo)) {
    if (v !== undefined && v !== "" && out[k] === undefined) {
      out[k] = v;
    }
  }

  const geoSourceForPriority =
    opts?.flags?.heroCtaSource || cfg?.heroCtaSource || out.cta_geo_source;
  const priorityLabel = resolveCtaGeoPriorityLabel(
    typeof geoSourceForPriority === "string" ? geoSourceForPriority : undefined,
  );
  if (priorityLabel) out.cta_geo_priority = priorityLabel;
  const geoHuman = formatCtaGeoSourceHuman(
    typeof geoSourceForPriority === "string" ? geoSourceForPriority : undefined,
  );
  if (geoHuman) out.cta_geo_source_human = geoHuman;
  const dest = resolveCtaDestinationPath(opts?.heroCtaType, {
    href:
      (typeof opts?.extra?.cta_href === "string" ? opts.extra.cta_href : undefined) ||
      opts?.flags?.ctaUrl ||
      cfg?.ctaUrl,
    isArgentina: (cfg?.country || out.worker_country) === "AR",
  });
  if (dest) out.cta_destination = dest;

  if (opts?.flags) {
    if (opts.flags.heroCtaResolved && !out.cta_resolved) {
      out.cta_resolved = opts.flags.heroCtaResolved;
    }
    if (opts.flags.heroCtaSource && !out.cta_geo_source) {
      out.cta_geo_source = opts.flags.heroCtaSource;
    }
    if (opts.flags.ctaUrl && !out.cta_href_config) {
      out.cta_href_config = opts.flags.ctaUrl.slice(0, 500);
    }
    if (opts.flags.ctaLabel && !out.cta_label_config) {
      out.cta_label_config = opts.flags.ctaLabel.slice(0, 120);
    }
  }

  if (opts?.extra) {
    for (const [k, v] of Object.entries(opts.extra)) {
      if (v !== undefined && v !== null) out[k] = v;
    }
  }

  return out;
}

function captureHeroCtaFunnelPostHog(
  kind: HeroCtaFunnelKind | "engagement_dropoff",
  extra: Record<string, string | number | boolean>,
): void {
  if (!isPostHogConfigured()) return;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ph = (window as any).posthog;
    if (typeof ph?.capture !== "function") return;
    ph.capture("hero_cta_funnel_event", {
      funnel_event_kind: kind,
      site: extra.brand ?? "delfuocampo",
      ...extra,
    });
  } catch {
    /* */
  }
}

function ensureHeroCtaRecording(force = false): void {
  if (!force) return;
  startSessionRecordingPostHog();
}

function postFunnelPayload(
  payload: Record<string, unknown>,
  opts?: { useBeacon?: boolean },
): void {
  if (!canPostFunnelApi()) return;
  const body = JSON.stringify(payload);
  try {
    if (opts?.useBeacon && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon("/api/track-funnel-failure", blob)) return;
    }
    void fetch("/api/track-funnel-failure", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body,
    }).catch(() => {});
  } catch {
    /* */
  }
}

export function postHeroCtaSlackFunnel(
  kind: HeroCtaFunnelKind,
  opts?: {
    templateName?: string;
    country?: string;
    sessionId?: string;
    lastStep?: string;
    timeOnPageSeconds?: number;
    heroCtaType?: HeroCtaResolvedType | string;
    elementId?: string;
    label?: string;
    href?: string;
    ctaResolved?: string;
    ctaGeoSource?: string;
    flags?: HeroCtaFlagsSnapshot;
    replayNearNow?: boolean;
    forceRecording?: boolean;
    extra?: Record<string, string | number | boolean>;
  },
): void {
  if (typeof window === "undefined") return;

  const replayNearNow =
    opts?.replayNearNow ??
    (kind.startsWith("cta_click_") || kind === "hero_cta_impression");

  ensureHeroCtaRecording(
    opts?.forceRecording === true ||
      kind === "page_enter_hero_landing" ||
      kind.startsWith("cta_click_"),
  );

  const phCtx = getPostHogSlackSessionPropertiesPostHog(
    replayNearNow ? { replayNearNow: true } : undefined,
  );
  const geo = getWorkerGeoAnalyticsProps();
  const country =
    opts?.country ??
    (geo.worker_country as string | undefined) ??
    String(
      getServerTemplateConfig()?.country ??
        (window as Window & { __TEMPLATE_CONFIG__?: { country?: string } })
          .__TEMPLATE_CONFIG__?.country ??
        "??",
    ).toUpperCase();

  const journeyPayload = getJourneyPayloadForServer();
  const mergedExtra = buildHeroCtaReportExtra({
    heroCtaType: opts?.heroCtaType,
    flags: opts?.flags,
    replayNearNow,
    extra: {
      ...(opts?.elementId ? { cta_element_id: opts.elementId } : {}),
      ...(opts?.label ? { cta_label: opts.label } : {}),
      ...(opts?.href ? { cta_href: opts.href.slice(0, 500) } : {}),
      ...(opts?.ctaResolved ? { cta_resolved: opts.ctaResolved } : {}),
      ...(opts?.ctaGeoSource ? { cta_geo_source: opts.ctaGeoSource } : {}),
      ...phCtx,
      ...journeyPayload,
      ...opts?.extra,
    },
  });

  const recordStep =
    kind === "page_enter_hero_landing"
      ? "hero_landing"
      : kind === "hero_cta_impression"
        ? `hero_impression:${opts?.heroCtaType ?? "unknown"}`
        : kind.startsWith("cta_click_hero_")
          ? `hero_click:${opts?.elementId ?? kind}`
          : undefined;

  captureVisitorJourneyRealtime({
    eventKind: kind,
    template: opts?.templateName,
    country: opts?.country,
    funnel: "hero_cta",
    productSurface: "hero",
    lastStep: opts?.lastStep,
    label: opts?.label,
    href: opts?.href,
    recordStep,
    intentLevel: kind.startsWith("cta_click_") ? "high_intent" : undefined,
    replayNearNow,
    extra: {
      hero_cta_type: opts?.heroCtaType,
      ...journeyPayload,
      ...(opts?.extra as Record<string, string | number | boolean> | undefined),
    },
  });

  const payload: Record<string, unknown> = {
    kind,
    template: opts?.templateName ?? "BaseTemplate",
    country,
    flow_trace_id: opts?.sessionId || phCtx.ph_session_id || undefined,
    last_step: opts?.lastStep,
    page_url: window.location.href,
    time_on_page_seconds: opts?.timeOnPageSeconds,
    extra: mergedExtra,
  };

  captureHeroCtaFunnelPostHog(kind, mergedExtra);
  postFunnelPayload(payload);
}

// ── Engagement watch: se fue sin clickear hero CTA ───────────────────────────

type HeroWatchCleanup = () => void;

let heroWatchCleanup: HeroWatchCleanup | null = null;

export function markHeroCtaClicked(): void {
  if (typeof window === "undefined") return;
  (window as Window & { __heroCtaClicked?: boolean }).__heroCtaClicked = true;
}

export function installHeroCtaEngagementWatch(opts: {
  templateName: string;
  country?: string;
  heroCtaType: HeroCtaResolvedType | string;
  flags?: HeroCtaFlagsSnapshot;
  ctaResolved?: string;
  ctaGeoSource?: string;
}): void {
  if (typeof window === "undefined" || !opts.heroCtaType) return;

  heroWatchCleanup?.();
  (window as Window & { __heroCtaClicked?: boolean }).__heroCtaClicked = false;

  const mountMs = Date.now();
  let exitSent = false;
  let maxScrollPct = 0;
  let firstInteractionMs: number | null = null;
  let tabBlurCount = 0;

  const markInteraction = () => {
    if (firstInteractionMs == null) firstInteractionMs = Date.now();
  };

  const onScroll = () => {
    markInteraction();
    const doc = document.documentElement;
    const scrollTop = window.scrollY || doc.scrollTop || 0;
    const height = Math.max(doc.scrollHeight - window.innerHeight, 1);
    const pct = Math.min(100, Math.round((scrollTop / height) * 100));
    if (pct > maxScrollPct) maxScrollPct = pct;
  };

  const handleExit = () => {
    if (exitSent) return;
    if ((window as Window & { __heroCtaClicked?: boolean }).__heroCtaClicked) return;

    const secOnPage = Math.max(0, Math.round((Date.now() - mountMs) / 1000));
    if (secOnPage < 4) return;

    exitSent = true;
    const exitReason =
      maxScrollPct >= 75
        ? "deep_scroll_no_click"
        : maxScrollPct >= 25
          ? "scrolled_no_click"
          : secOnPage >= 45
            ? "long_dwell_no_click"
            : "no_click_exit";

    const replayNearNow = true;
    const phCtx = getPostHogSlackSessionPropertiesPostHog({ replayNearNow: true });
    const mergedExtra = buildHeroCtaReportExtra({
      heroCtaType: opts.heroCtaType,
      flags: opts.flags,
      replayNearNow,
      extra: {
        hero_cta: opts.heroCtaType,
        exit_reason: exitReason,
        seconds_on_page: secOnPage,
        max_scroll_pct: maxScrollPct,
        scroll_attempted: maxScrollPct > 8,
        tab_blur_count: tabBlurCount,
        ...(firstInteractionMs != null
          ? {
              time_to_first_interaction_s: Math.max(
                0,
                Math.round((firstInteractionMs - mountMs) / 1000),
              ),
            }
          : {}),
        ...(opts.ctaResolved ? { cta_resolved: opts.ctaResolved } : {}),
        ...(opts.ctaGeoSource ? { cta_geo_source: opts.ctaGeoSource } : {}),
        ...phCtx,
      },
    });

    const payload = {
      kind: "engagement_dropoff",
      template: opts.templateName,
      country:
        opts.country ??
        String(getServerTemplateConfig()?.country ?? "??").toUpperCase(),
      flow_trace_id: phCtx.ph_session_id,
      page_url: window.location.href,
      time_on_page_seconds: secOnPage,
      extra: mergedExtra,
    };

    captureHeroCtaFunnelPostHog("engagement_dropoff", mergedExtra);
    postFunnelPayload(payload, { useBeacon: true });
  };

  const onVisibility = () => {
    if (document.visibilityState === "hidden") {
      tabBlurCount += 1;
      handleExit();
    }
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("pointerdown", markInteraction, { passive: true });
  window.addEventListener("keydown", markInteraction, { passive: true });
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", handleExit);

  heroWatchCleanup = () => {
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("pointerdown", markInteraction);
    window.removeEventListener("keydown", markInteraction);
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("pagehide", handleExit);
  };
}

export function uninstallHeroCtaEngagementWatch(): void {
  heroWatchCleanup?.();
  heroWatchCleanup = null;
}